import React, { FC, useMemo, useState } from 'react';
import { BuilderComponent, OpeningHour, useNode, BuilderComponentProps } from '@mnz/builder';
import { Switch, useTheme } from '@mui/material';
import { ResponsiveStyle } from '../../core/StyledComponent';
import { boxCss, cardContentCss, cardCss, cardMediaCss, descriptionCss, labelCss } from '../../common/style';
import EditOpeningHoursModal from './EditOpeningHourModal';
import HourCard from './HourCard';

export interface DayOfWeek {
    value: number;
    label: string;
    openingHours: null | OpeningHour;
}

const initDaysOfWeek: DayOfWeek[] = [
    { value: 1, label: 'Monday', openingHours: null },
    { value: 2, label: 'Tuesday', openingHours: null },
    { value: 3, label: 'Wednesday', openingHours: null },
    { value: 4, label: 'Thursday', openingHours: null },
    { value: 5, label: 'Friday', openingHours: null },
    { value: 6, label: 'Saturday', openingHours: null },
    { value: 0, label: 'Sunday', openingHours: null },
];

export interface OpeningHoursStyle extends ResponsiveStyle {
    label: ResponsiveStyle;
    dayOfWeek: ResponsiveStyle;
    timeRange: ResponsiveStyle;
}

export interface OpeningHoursSettingProps extends BuilderComponentProps, ResponsiveStyle {
    text: string;
    hideDay: Record<number, boolean>;
    style?: OpeningHoursStyle;
}

export const OpeningHoursSetting: FC<BuilderComponent<OpeningHoursSettingProps>> = () => {
    const [selectedOpeningHours, setSelectedOpeningHours] = useState<DayOfWeek | null>(null);
    const [openModalEdit, setOpenModalEdit] = useState(false);

    const theme = useTheme();
    const {
        actions: { setProp, setAccount, setHidden },
        props: { account, ...props },
        hidden,
    } = useNode((node) => {
        return {
            props: node.data.props as unknown as OpeningHoursSettingProps,
            hidden: node.data.hidden,
        };
    });

    const openingHoursList = useMemo(() => {
        return initDaysOfWeek.map((day) => {
            const d = account.openingHours.find((d) => d.dayOfWeek === day.value);
            return {
                value: day.value,
                label: day.label,
                openingHours: d || {
                    dayOfWeek: day.value,
                    times: [],
                    allDay: false,
                },
            };
        });
    }, [account.openingHours]);

    const handleToggleOpeningHour = async (day: number, checked: boolean) => {
        setProp<OpeningHoursSettingProps>((props) => (props.hideDay[day] = !checked));
    };
    const handleUpdateOpeningHours = async (value: DayOfWeek) => {
        const data = openingHoursList.map((d) => {
            if (d.value === value.value) {
                return value;
            } else {
                return d;
            }
        });

        setAccount({
            openingHours: data.map((d) => d.openingHours),
        });
        setSelectedOpeningHours(data.find((d) => d.value === value.value));
    };

    const onSelectOpeningHours = (value: DayOfWeek) => {
        setOpenModalEdit(true);
        setSelectedOpeningHours(value);
    };

    return (
        <>
            <div css={[boxCss(theme), { marginBottom: 40, marginTop: 20 }]}>
                <div css={cardCss()}>
                    <div css={cardContentCss()}>
                        <p css={labelCss()}>Opening Hours</p>
                        <p css={descriptionCss(theme)}>Show opening hours on menu</p>
                    </div>
                    <div css={cardMediaCss()}>
                        <Switch checked={!hidden} onChange={(e) => setHidden(!e.target.checked)} />
                    </div>
                </div>
                {openingHoursList.map((day) => (
                    <HourCard
                        key={day.value}
                        display={!props.hideDay[day.value]}
                        day={day}
                        setSelectedOpeningHours={onSelectOpeningHours}
                        handleToggleOpeningHours={handleToggleOpeningHour}
                    />
                ))}
            </div>
            <EditOpeningHoursModal
                open={openModalEdit}
                selectedOpeningHours={selectedOpeningHours}
                onClose={() => {
                    setOpenModalEdit(false);
                    setSelectedOpeningHours(null);
                }}
                onBack={() => {
                    setOpenModalEdit(false);
                    setSelectedOpeningHours(null);
                }}
                onUpdate={(o) => handleUpdateOpeningHours({ ...selectedOpeningHours, openingHours: o })}
            />
        </>
    );
};
